export const defaultSchedule = timezone => ({
  frequency: 'daily',
  time: '10:00',
  timezone,
  weekdays: [1, 2, 3, 4, 5],
  date: '',
});

export const validSchedule = schedule => {
  if (!schedule || !['once', 'daily', 'weekly'].includes(schedule.frequency))
    return false;
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.time || '')) return false;
  try {
    if (!schedule.timezone) return false;
    Intl.DateTimeFormat('en', {
      timeZone: schedule.timezone,
    }).resolvedOptions();
  } catch {
    return false;
  }
  if (schedule.frequency === 'once') {
    const date = new Date(`${schedule.date}T00:00:00Z`);
    return (
      /^\d{4}-\d{2}-\d{2}$/.test(schedule.date || '') &&
      !Number.isNaN(date.getTime()) &&
      date.toISOString().slice(0, 10) === schedule.date &&
      date.getUTCFullYear() >= 2000 &&
      date.getUTCFullYear() <= 2100
    );
  }
  return (
    schedule.frequency === 'daily' ||
    (Array.isArray(schedule.weekdays) &&
      schedule.weekdays.length > 0 &&
      schedule.weekdays.every(
        day => Number.isInteger(day) && day >= 1 && day <= 7
      ))
  );
};

export const scheduleSummary = (data, t) => {
  const schedule = data.schedule;
  if (!validSchedule(schedule)) return t('startSchedule.incomplete');
  let frequency = t('startSchedule.daily');
  if (schedule.frequency === 'once') frequency = schedule.date;
  if (schedule.frequency === 'weekly')
    frequency = [...new Set(schedule.weekdays)]
      .sort()
      .map(day => t(`startSchedule.day${day}`))
      .join(', ');
  return `${frequency} · ${schedule.time} · ${schedule.timezone}`;
};

export const startMessages = {
  en: {
    trigger: 'Workflow start',
    scheduled: 'Scheduled start',
    startSchedule: {
      rules: 'How scheduled starts work',
      mode: 'Start mode',
      event: 'On event',
      schedule: 'On schedule',
      frequency: 'Repeat',
      once: 'Once',
      daily: 'Daily',
      weekly: 'On selected weekdays',
      date: 'Date',
      time: 'Time',
      timezone: 'Time zone',
      weekdays: 'Days of week',
      entity: 'Who enters the workflow',
      patients: 'Patients',
      appointments: 'Appointments',
      day1: 'Mon',
      day2: 'Tue',
      day3: 'Wed',
      day4: 'Thu',
      day5: 'Fri',
      day6: 'Sat',
      day7: 'Sun',
      patientHint:
        'One run per patient. Appointment fields and products are unavailable; select an explicit message source.',
      appointmentHint:
        'One run per appointment. Conditions check that appointment, its patient and its products.',
      hint: 'Each occurrence starts a new run. Conditions select recipients. Missed recurring dates are not replayed; pausing stops new starts.',
      testHint:
        'Sending remains limited to verified test patients. Saving a draft does not activate the workflow.',
      timezoneHint:
        'This time zone controls the start. Date conditions use the time zone configured for the connection.',
      incomplete: 'Configure schedule',
      patientRecord: 'Patient #{id}',
      patientTemplate:
        'Use a patient template without appointment, doctor, branch or calendar variables.',
    },
    scheduleErrors: {
      schedule_expired: 'Choose a future date and time for a one-time start.',
      invalid_schedule:
        'Choose frequency, time, time zone and date or weekdays.',
      appointment_context_unavailable:
        'This step needs an appointment. For patient starts, select patient conditions or a fixed wait.',
      patient_source_required:
        'Select an explicit message source: a patient start has no appointment branch.',
      patient_template_required:
        'The template requires an appointment. Choose a patient-only template.',
    },
  },
  ru: {
    trigger: 'Запуск сценария',
    scheduled: 'Запуск по расписанию',
    startSchedule: {
      rules: 'Как работает запуск по расписанию',
      mode: 'Режим запуска',
      event: 'По событию',
      schedule: 'По расписанию',
      frequency: 'Повторять',
      once: 'Один раз',
      daily: 'Ежедневно',
      weekly: 'По дням недели',
      date: 'Дата',
      time: 'Время',
      timezone: 'Часовой пояс',
      weekdays: 'Дни недели',
      entity: 'Кого запускать в сценарий',
      patients: 'Пациенты',
      appointments: 'Записи на приём',
      day1: 'Пн',
      day2: 'Вт',
      day3: 'Ср',
      day4: 'Чт',
      day5: 'Пт',
      day6: 'Сб',
      day7: 'Вс',
      patientHint:
        'Один запуск на пациента. Поля записи и её товаров недоступны; для сообщений выберите конкретный источник.',
      appointmentHint:
        'Один запуск на запись. Условия проверяют именно эту запись, её пациента и товары.',
      hint: 'Каждое повторение — новый запуск. Получателей отбирает блок «Условие». Пропущенные дни не догоняются; пауза останавливает новые запуски.',
      testHint:
        'Отправка по-прежнему доступна только проверенным тестовым пациентам. Сохранение черновика не активирует сценарий.',
      timezoneHint:
        'Этот пояс задаёт время запуска. Условия по датам используют часовой пояс подключения.',
      incomplete: 'Настройте расписание',
      patientRecord: 'Пациент №{id}',
      patientTemplate:
        'Выберите шаблон для пациента — без данных приёма, врача, филиала и календарного вложения.',
    },
    scheduleErrors: {
      schedule_expired: 'Для разового запуска выберите будущую дату и время.',
      invalid_schedule:
        'Выберите периодичность, время, часовой пояс и дату либо дни недели.',
      appointment_context_unavailable:
        'Блоку нужна запись на приём. Для запуска пациентов выберите условия пациента или фиксированное ожидание.',
      patient_source_required:
        'Выберите конкретный источник отправки: у запуска пациента нет филиала записи.',
      patient_template_required:
        'Шаблону нужна запись на приём. Выберите шаблон только с данными пациента.',
    },
  },
};
